import { ReactNode } from "react"

import {
  Column,
  ColumnInstance,
  IdType,
  useColumnOrder,
  useTable as useReactTable,
} from "react-table"
import type { BaseRecord, HeaderGroup, TableProps } from "./types"
import { css, cx } from "@emotion/css"

import { Row } from "./Row"
import { getDefaultRowKey, getTrimmedId, sorted } from "./utils"
import { useDataKeys } from "./atoms"
import { useTable } from "./useTable"
import { CSSProperties } from "@material-ui/core/styles/withStyles"
import Button from "ui/buttons/Button"
import { Columns } from "components/tables/Columns"
import { useLocalStorage } from "hooks/useLocalStorage"
import { useI18n } from "providers/I18n"

export function Table<T extends BaseRecord>(props: TableProps<T>) {
  const { t } = useI18n()
  const {
    id,
    columns,
    data,
    getRowKey = getDefaultRowKey,
    className,
    NewRow,
    filters,
    onOpen,
    allowed,
    open,
    errors,
  } = props

  const [storedOrder, setStoredOrder] = useLocalStorage(`${id}-order`, null)

  const { getTableProps, headers, setColumnOrder } = useReactTable<T>(
    {
      columns,
      data,
    } as { columns: Column<T>[]; data: T[] },
    useColumnOrder,
  )

  const { headerGroups } = useTable<T>({
    data,
    columns: columns
      .map((c) => ({
        ...c,
        hidden: !headers?.find(
          (h) => getTrimmedId(h?.id) === getTrimmedId(c?.id),
        )?.isVisible,
      }))
      ?.sort(sorted(storedOrder)),
    getRowKey,
  })

  const handleColumnChange = (columnOrder: Array<IdType<T>>) => {
    setColumnOrder(columnOrder)
    setStoredOrder(columnOrder)
  }

  const _headers = headers
    ?.map((h) => ({ ...h, id: getTrimmedId(h?.id) }))
    .sort(sorted(storedOrder))

  const handleOpen = () => {
    onOpen()
  }

  return (
    <>
      <div className={styles.filtersWrapper}>
        {filters}
        <div className={styles.columns}>
          <Columns<T>
            data={
              _headers as Array<
                ColumnInstance<T> & {
                  header: string
                  fixed: boolean
                }
              >
            }
            onOrderChange={handleColumnChange}
            hasHiddenCols={false}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpen}
            disabled={!allowed || open}
          >
            {t("rate-card.create_button")}
          </Button>
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <table className={cx(styles.table, className)} {...getTableProps()}>
          <HeaderGroups headerGroups={headerGroups} />
          <RowGroup component={NewRow} />
        </table>
        {errors && (
          <span className="BodyBody-xsRedRegular">{t(errors["row"])}</span>
        )}
      </div>
    </>
  )
}

function HeaderGroups(props: { headerGroups: HeaderGroup[] }) {
  const durations = props.headerGroups?.[1]?.headers?.filter(
    (h) => typeof h.Header === "string",
  )?.length
  return (
    <thead style={{ "--duration-number": `${durations}` } as CSSProperties}>
      {props.headerGroups.map((group) => (
        <tr key={group.id} role="row">
          {group.headers.map((column) => {
            return (
              <th
                key={column.id}
                role="columnheader"
                colSpan={column.span ?? 1}
                style={{
                  width: column.width,
                  borderRight: column.border,
                  borderLeft: column.border,
                }}
              >
                <div>{column.Header}</div>
              </th>
            )
          })}
        </tr>
      ))}
    </thead>
  )
}

const RowGroup = ({ component }: { component?: ReactNode }) => {
  const dataKeys = useDataKeys()

  return (
    <tbody>
      {component}
      {dataKeys.map((rowId, index) => {
        return <Row key={rowId} id={rowId} index={index} />
      })}
    </tbody>
  )
}

const styles = {
  filtersWrapper: css`
    display: flex;
    justify-content: space-between;
  `,
  columns: css`
    display: flex;
    gap: var(--spacing-m);
    margin-bottom: var(--spacing-xl);
    > div {
      display: flex;
    }
  `,
  tableWrapper: css`
    max-width: 100%;
    max-height: calc(15 * 40px);
    overflow: auto;
    padding-bottom: 15px; // scrollbar room
    ::-webkit-scrollbar {
      width: 12px;
      height: 12px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 6px;
    }
  `,
  table: css`
    border-spacing: 0;
    border-collapse: collapse;
    border: 1px solid var(--border-color-2);
    width: 100%;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
      th:nth-last-child(--duration-number) {
        border-left: 1px solid var(--border-color-2);
      }
    }

    th,
    td {
      margin: 0;
      padding: 0 8px;
      height: 40px;
      border-bottom: 1px solid var(--border-color-2);
      border-right: 1px solid var(--border-color-2);
      text-align: center;

      :last-child {
        border-right: 0;
      }

      &.right {
        text-align: right;
      }
      &.left {
        text-align: left;
      }
    }

    th {
      font-size: 12px;
      font-weight: bold;
    }

    td {
      font-size: 13px;
      font-weight: normal;
    }
  `,
}
